import { Slider as MantineSlider, SliderProps, useMantineTheme } from '@mantine/core';
import { forwardRef } from 'react';
import { usePreferencesStore } from '../../../stores';
import { defaultColorScheme } from '../../../theme/theme';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedSliderProps extends SliderProps {
  gradientVariant?: GradientVariant;
  useColorScheme?: boolean; // Use color scheme from state management
}

const Slider = forwardRef<HTMLDivElement, ThemedSliderProps>(
  ({ gradientVariant = 'primary', useColorScheme = true, styles, ...rest }, ref) => {
    const theme = useMantineTheme();
    const colorSchemeKey = usePreferencesStore((state) => state.colorSchemeKey);
    const reducedMotion = usePreferencesStore((state) => state.reducedMotion);
    const strongFocus = usePreferencesStore((state) => state.strongFocus);

    // Determine which color to use
    let color = theme.other.gradients[gradientVariant].from;

    if (useColorScheme) {
      const effectiveColorSchemeKey = colorSchemeKey || defaultColorScheme.key;
      const colorScheme = theme.other.colorSchemes?.find((cs: any) => cs.key === effectiveColorSchemeKey);
      if (colorScheme) {
        color = colorScheme.primary.from;
      }
    }

    return (
      <MantineSlider
        ref={ref}
        styles={{
          ...styles,
          bar: {
            backgroundColor: `${color} !important`,
            ...(styles as any)?.bar,
          },
          thumb: {
            transition: reducedMotion ? 'none' : 'transform 0.15s ease, box-shadow 0.15s ease',
            borderColor: `${color} !important`,
            backgroundColor: 'white',
            '&:focus': {
              outline: strongFocus ? `2px solid ${color} !important` : 'none',
              outlineOffset: strongFocus ? '2px' : '0',
              boxShadow: `0 0 0 4px ${color}30 !important`,
            },
            '&:focus-within': {
              outline: strongFocus ? `2px solid ${color} !important` : 'none',
            },
            ...(styles as any)?.thumb,
          },
        }}
        {...rest}
      />
    );
  }
);

Slider.displayName = 'Slider';

export default Slider;
